/**
 * Enhanced application routing with error boundaries
 * Wraps each route with appropriate error boundary protection
 */

import { Suspense } from 'react';
import { Routes, Route, Link, Navigate } from 'react-router-dom';

import { EmailCtaRedirect } from './components/email/EmailCtaRedirect';
import { EmailCtaRedirectToChat } from './components/email/EmailCtaRedirectToChat';
import { ONBOARDING_DAY1_EMAIL_CTAS } from './constants/onboardingEmailCtas';
import { OnboardingProvider } from './lib/OnboardingContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { RequireBasePlanRoute } from './components/auth/RequireBasePlanRoute';
import { LoadingSpinnerFullscreen } from './components/ui/LoadingSpinner';
import { OktaCallback } from './screens/OktaCallback/OktaCallback';
import { MicrosoftCallback } from './screens/MicrosoftCallback/MicrosoftCallback';
import { RoleProtectedRoute } from './components/auth/RoleProtectedRoute';
import { PersonalBillingProtectedRoute } from './components/auth/PersonalBillingProtectedRoute';
import RouteErrorBoundary from './components/ErrorBoundary/RouteErrorBoundary';
import { StripeFeatureErrorBoundary } from './components/StripeErrorBoundary';

import { createLazyComponent, routePreloader } from './utils/lazyLoader';

// Lazy-loaded components for code splitting with strategic preloading
const { Component: Login } = createLazyComponent(() =>
  import('./screens/Login/Login').then((module) => ({ default: module.Login })),
);

const { Component: CreateAccount, preload: preloadCreateAccount } = createLazyComponent(() =>
  import('./screens/CreateAccount/CreateAccount').then((module) => ({
    default: module.CreateAccount,
  })),
);

const { Component: EmailVerificationPage } = createLazyComponent(() =>
  import('./screens/EmailVerification/EmailVerificationPage').then((module) => ({
    default: module.EmailVerificationPage,
  })),
);

const { Component: ForgotPassword } = createLazyComponent(() =>
  import('./screens/ForgotPassword/ForgotPassword').then((module) => ({
    default: module.ForgotPassword,
  })),
);

const { Component: ResetPasswordPage } = createLazyComponent(() =>
  import('./screens/ResetPassword/ResetPasswordPage').then((module) => ({
    default: module.ResetPasswordPage,
  })),
);

// OktaCallback is now imported directly above

const { Component: Chat, preload: preloadChat } = createLazyComponent(() =>
  import('./screens/Chat/Chat').then((module) => ({ default: module.Chat })),
);

const { Component: ProfileLayout } = createLazyComponent(() =>
  import('./screens/Profile/ProfileLayout').then((module) => ({
    default: module.ProfileLayout,
  })),
);

const { Component: ProfileEdit } = createLazyComponent(() =>
  import('./screens/Profile/ProfileEdit').then((module) => ({
    default: module.ProfileEdit,
  })),
);

const { Component: Password } = createLazyComponent(() =>
  import('./screens/Profile/Password').then((module) => ({
    default: module.Password,
  })),
);

const { Component: Authentication } = createLazyComponent(() =>
  import('./screens/Profile/Authentication').then((module) => ({
    default: module.Authentication,
  })),
);

const { Component: Billing } = createLazyComponent(() =>
  import('./screens/Profile/Billing').then((module) => ({
    default: module.Billing,
  })),
);

const { Component: Checkout } = createLazyComponent(() =>
  import('./screens/Payment/Checkout').then((module) => ({
    default: module.Checkout,
  })),
);

const { Component: Return } = createLazyComponent(() =>
  import('./screens/Payment/Return').then((module) => ({
    default: module.Return,
  })),
);

const { Component: Pricing } = createLazyComponent(() =>
  import('./screens/Profile/Pricing').then((module) => ({
    default: module.Pricing,
  })),
);

const { Component: LoginHistory } = createLazyComponent(() =>
  import('./screens/Profile/LoginHistory').then((module) => ({
    default: module.LoginHistory,
  })),
);

const { Component: PlanUsage } = createLazyComponent(() =>
  import('./screens/Profile/PlanUsage').then((module) => ({
    default: module.PlanUsage,
  })),
);

const { Component: Integrations } = createLazyComponent(() =>
  import('./screens/Profile/Integrations').then((module) => ({
    default: module.Integrations,
  })),
);

const { Component: AcceptTermsPage } = createLazyComponent(() =>
  import('./screens/AcceptTerms/AcceptTermsPage').then((module) => ({
    default: module.AcceptTermsPage,
  })),
);

const { Component: EmailMarketingPreferencesPage } = createLazyComponent(() =>
  import('./screens/EmailMarketingPreferences/EmailMarketingPreferencesPage').then((module) => ({
    default: module.EmailMarketingPreferencesPage,
  })),
);

const { Component: UserOnboarding } = createLazyComponent(() =>
  import('./screens/Onboarding/UserOnboarding').then((module) => ({
    default: module.UserOnboarding,
  })),
);

const { Component: Marketplace } = createLazyComponent(() =>
  import('./screens/Store').then((module) => ({ default: module.Store })),
);

const { Component: IntegrationDetail } = createLazyComponent(() =>
  import('./screens/Store/IntegrationDetail').then((module) => ({
    default: module.IntegrationDetail,
  })),
);

const { Component: Members } = createLazyComponent(() =>
  import('./screens/Profile/Members/Members').then((module) => ({
    default: module.Members,
  })),
);

const { Component: Industry } = createLazyComponent(() =>
  import('./screens/Profile/IndustryAndAIModel/Industry').then((module) => ({
    default: module.Industry,
  })),
);

const { Component: AIProviders } = createLazyComponent(() =>
  import('./screens/Profile/AIProviders/AIProviders').then((module) => ({
    default: module.AIProviders,
  })),
);

const { Component: Layout } = createLazyComponent(() =>
  import('./screens/Profile/Layout/Layout').then((module) => ({
    default: module.Layout,
  })),
);

const { Component: AuditLog } = createLazyComponent(() =>
  import('./screens/Profile/AuditLog/AuditLog').then((module) => ({
    default: module.AuditLog,
  })),
);

const { Component: Organization } = createLazyComponent(() =>
  import('./screens/Profile/Organization/Organization').then((module) => ({
    default: module.Organization,
  })),
);

const { Component: TeamUsage } = createLazyComponent(() =>
  import('./screens/Profile/TeamUsage').then((module) => ({
    default: module.TeamUsage,
  })),
);

const { Component: EnterpriseBilling } = createLazyComponent(() =>
  import('./screens/Profile/EnterpriseBilling').then((module) => ({
    default: module.EnterpriseBilling,
  })),
);

const { Component: OrganizationIntake } = createLazyComponent(() =>
  import('./screens/Profile/OrganizationIntake').then((module) => ({
    default: module.OrganizationIntake,
  })),
);

const { Component: OrganizationSetupPending } = createLazyComponent(() =>
  import('./screens/Profile/OrganizationSetupPending').then((module) => ({
    default: module.OrganizationSetupPending,
  })),
);

const { Component: AgenticAIConfig } = createLazyComponent(() =>
  import('./screens/Profile/AgenticAIConfig').then((module) => ({
    default: module.AgenticAIConfig,
  })),
);

const { Component: AcceptInvite } = createLazyComponent(() =>
  import('./screens/AcceptInvite/AcceptInvite').then((module) => ({
    default: module.AcceptInvite,
  })),
);

routePreloader.register('/chat', preloadChat);
routePreloader.register('/signup', preloadCreateAccount);

/** Lazy onboarding must sit under OnboardingProvider so `useOnboarding` always resolves (same module boundary as the route). */
const UserOnboardingWithProvider = () => (
  <OnboardingProvider>
    <UserOnboarding />
  </OnboardingProvider>
);

/**
 * Wrapper component for routes with error boundaries
 */
const RouteWrapper = ({
  children,
  routeName,
  fallbackRoute,
}: {
  children: React.ReactNode;
  routeName: string;
  fallbackRoute?: string;
}) => (
  <RouteErrorBoundary routeName={routeName} fallbackRoute={fallbackRoute}>
    {children}
  </RouteErrorBoundary>
);

/**
 * AppRoutes component with comprehensive error boundary protection
 */
export const AppRoutesWithErrorBoundaries = () => {
  const emailCtaToChat = (
    <RouteWrapper routeName='Chat'>
      <ProtectedRoute>
        <RequireBasePlanRoute>
          <EmailCtaRedirectToChat />
        </RequireBasePlanRoute>
      </ProtectedRoute>
    </RouteWrapper>
  );

  const emailCtaTo = (to: string, routeName: string, fallbackRoute: string) => (
    <RouteWrapper routeName={routeName} fallbackRoute={fallbackRoute}>
      <ProtectedRoute>
        <RequireBasePlanRoute>
          <EmailCtaRedirect to={to} />
        </RequireBasePlanRoute>
      </ProtectedRoute>
    </RouteWrapper>
  );

  return (
    <Suspense fallback={<LoadingSpinnerFullscreen size='xl' text='Loading your account...' />}>
      <Routes>
        {/* Public routes - redirect to chat if authenticated */}
        <Route
          path='/'
          element={
            <RouteWrapper routeName='Home' fallbackRoute='/chat'>
              <ProtectedRoute requireAuth={false}>
                <Login />
              </ProtectedRoute>
            </RouteWrapper>
          }
        />
        <Route
          path='/login'
          element={
            <RouteWrapper routeName='Login' fallbackRoute='/chat'>
              <ProtectedRoute requireAuth={false}>
                <Login />
              </ProtectedRoute>
            </RouteWrapper>
          }
        />
        <Route
          path='/signup'
          element={
            <RouteWrapper routeName='Sign Up' fallbackRoute='/login'>
              <ProtectedRoute requireAuth={false}>
                <CreateAccount />
              </ProtectedRoute>
            </RouteWrapper>
          }
        />
        <Route
          path='/verify-email'
          element={
            <RouteWrapper routeName='Email Verification' fallbackRoute='/login'>
              <ProtectedRoute requireAuth={false}>
                <EmailVerificationPage />
              </ProtectedRoute>
            </RouteWrapper>
          }
        />
        <Route
          path='/forgot-password'
          element={
            <RouteWrapper routeName='Password Reset' fallbackRoute='/login'>
              <ProtectedRoute requireAuth={false}>
                <ForgotPassword />
              </ProtectedRoute>
            </RouteWrapper>
          }
        />
        <Route
          path='/reset-password'
          element={
            <RouteWrapper routeName='Confirm Password Reset' fallbackRoute='/login'>
              <ProtectedRoute requireAuth={false}>
                <ResetPasswordPage />
              </ProtectedRoute>
            </RouteWrapper>
          }
        />
        <Route
          path='/email-marketing-preferences'
          element={
            <RouteWrapper routeName='Email preferences' fallbackRoute='/login'>
              {/* requireAuth={false}: logged-in users are not sent to chat except on /, /login, /signup */}
              <ProtectedRoute requireAuth={false}>
                <EmailMarketingPreferencesPage />
              </ProtectedRoute>
            </RouteWrapper>
          }
        />
        <Route
          path='/auth/okta/callback'
          element={
            <RouteWrapper routeName='Okta Callback' fallbackRoute='/login'>
              <OktaCallback />
            </RouteWrapper>
          }
        />
        <Route
          path='/auth/microsoft/callback'
          element={
            <RouteWrapper routeName='Microsoft Callback' fallbackRoute='/login'>
              <MicrosoftCallback />
            </RouteWrapper>
          }
        />

        <Route
          path='/accept-invite'
          element={
            <RouteWrapper routeName='Accept Invitation' fallbackRoute='/chat'>
              <AcceptInvite />
            </RouteWrapper>
          }
        />

        <Route
          path='/org-setup-pending'
          element={
            <RouteWrapper routeName='Organization Setup Pending' fallbackRoute='/profile'>
              <ProtectedRoute>
                <OrganizationSetupPending />
              </ProtectedRoute>
            </RouteWrapper>
          }
        />
        <Route
          path='/onboarding'
          element={
            <RouteWrapper routeName='Organization Onboarding' fallbackRoute='/profile'>
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={['admin', 'owner']}>
                  <OrganizationIntake />
                </RoleProtectedRoute>
              </ProtectedRoute>
            </RouteWrapper>
          }
        />

        {/*
          Email / onboarding CTA entry points: query params preserved (UTM attribution).
          Day 1 CTAs are defined in `constants/onboardingEmailCtas.ts` (titles + `/email/...` paths).

          Legacy `/upload`, `/summarize`, `/search`, `/automation` remain for links already sent;
          prefer `/email/*` for anything new so the intent is obvious in analytics and support.
        */}
        <Route path='/email/upload' element={emailCtaToChat} />
        {ONBOARDING_DAY1_EMAIL_CTAS.map((cta) => (
          <Route
            key={cta.emailPath}
            path={cta.emailPath}
            element={
              cta.destinationPath === '/chat'
                ? emailCtaToChat
                : emailCtaTo(cta.destinationPath, `Email CTA — ${cta.title}`, cta.destinationPath)
            }
          />
        ))}
        <Route path='/email/search' element={emailCtaToChat} />
        <Route path='/email/automation' element={emailCtaToChat} />
        <Route path='/upload' element={emailCtaToChat} />
        <Route path='/summarize' element={emailCtaToChat} />
        <Route path='/search' element={emailCtaToChat} />
        <Route path='/automation' element={emailCtaToChat} />

        <Route
          path='/chat'
          element={
            <RouteWrapper routeName='Chat'>
              <ProtectedRoute>
                <RequireBasePlanRoute>
                  <Chat />
                </RequireBasePlanRoute>
              </ProtectedRoute>
            </RouteWrapper>
          }
        />
        <Route
          path='/marketplace'
          element={
            <RouteWrapper routeName='Marketplace' fallbackRoute='/chat'>
              <ProtectedRoute>
                <RequireBasePlanRoute>
                  <Marketplace />
                </RequireBasePlanRoute>
              </ProtectedRoute>
            </RouteWrapper>
          }
        />
        <Route
          path='/marketplace/:integrationId'
          element={
            <RouteWrapper routeName='Integration Detail' fallbackRoute='/marketplace'>
              <ProtectedRoute>
                <RequireBasePlanRoute>
                  <IntegrationDetail />
                </RequireBasePlanRoute>
              </ProtectedRoute>
            </RouteWrapper>
          }
        />
        <Route
          path='/profile'
          element={
            <RouteWrapper routeName='Profile' fallbackRoute='/chat'>
              <ProtectedRoute>
                <ProfileLayout />
              </ProtectedRoute>
            </RouteWrapper>
          }
        >
          <Route index element={<ProfileEdit />} />
          <Route
            path='billing'
            element={
              <PersonalBillingProtectedRoute>
                <Billing />
              </PersonalBillingProtectedRoute>
            }
          />
          <Route
            path='pricing'
            element={
              <PersonalBillingProtectedRoute>
                <StripeFeatureErrorBoundary featureName='Pricing'>
                  <Pricing />
                </StripeFeatureErrorBoundary>
              </PersonalBillingProtectedRoute>
            }
          />
          <Route path='password' element={<Password />} />
          <Route path='authentication' element={<Authentication />} />
          <Route path='login-history' element={<LoginHistory />} />
          <Route path='plan-usage' element={<PlanUsage />} />
          <Route
            path='team-usage'
            element={
              <RoleProtectedRoute allowedRoles={['admin', 'owner']}>
                <TeamUsage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path='enterprise-billing'
            element={
              <RoleProtectedRoute allowedRoles={['admin', 'owner']}>
                <StripeFeatureErrorBoundary featureName='Billing & Subscriptions'>
                  <EnterpriseBilling />
                </StripeFeatureErrorBoundary>
              </RoleProtectedRoute>
            }
          />
          <Route
            path='integrations'
            element={
              <RequireBasePlanRoute>
                <Integrations />
              </RequireBasePlanRoute>
            }
          />
          <Route
            path='organization'
            element={
              <RoleProtectedRoute allowedRoles={['admin', 'owner']}>
                <Organization />
              </RoleProtectedRoute>
            }
          />
          <Route
            path='members'
            element={
              <RoleProtectedRoute allowedRoles={['admin', 'owner']}>
                <Members />
              </RoleProtectedRoute>
            }
          />
          <Route
            path='data-retention'
            element={<Navigate to='/profile/organization?section=retention' replace />}
          />
          <Route
            path='industry'
            element={
              <RoleProtectedRoute allowedRoles={['admin', 'owner']}>
                <Industry />
              </RoleProtectedRoute>
            }
          />
          <Route
            path='ai-providers'
            element={
              <RoleProtectedRoute allowedRoles={['admin', 'owner']}>
                <AIProviders />
              </RoleProtectedRoute>
            }
          />
          <Route
            path='agentic-ai-config'
            element={
              <RoleProtectedRoute allowedRoles={['admin', 'owner']}>
                <AgenticAIConfig />
              </RoleProtectedRoute>
            }
          />
          <Route
            path='layout'
            element={
              <RoleProtectedRoute allowedRoles={['admin', 'owner']}>
                <Layout />
              </RoleProtectedRoute>
            }
          />
          <Route
            path='audit-log'
            element={
              <RoleProtectedRoute allowedRoles={['admin', 'owner']}>
                <AuditLog />
              </RoleProtectedRoute>
            }
          />
        </Route>
        <Route
          path='/accept-terms'
          element={
            <RouteWrapper routeName='Accept Terms' fallbackRoute='/chat'>
              <ProtectedRoute>
                <AcceptTermsPage />
              </ProtectedRoute>
            </RouteWrapper>
          }
        />
        <Route
          path='/user-onboarding'
          element={
            <RouteWrapper routeName='User Onboarding' fallbackRoute='/chat'>
              <ProtectedRoute>
                <UserOnboardingWithProvider />
              </ProtectedRoute>
            </RouteWrapper>
          }
        />
        <Route
          path='/goal-selection'
          element={
            <RouteWrapper
              routeName='Legacy Goal Selection Redirect'
              fallbackRoute='/user-onboarding'
            >
              <ProtectedRoute>
                <UserOnboardingWithProvider />
              </ProtectedRoute>
            </RouteWrapper>
          }
        />
        <Route
          path='/industry-selection'
          element={
            <RouteWrapper
              routeName='Legacy Industry Selection Redirect'
              fallbackRoute='/user-onboarding'
            >
              <ProtectedRoute>
                <UserOnboardingWithProvider />
              </ProtectedRoute>
            </RouteWrapper>
          }
        />
        <Route
          path='/workspace-selection'
          element={
            <RouteWrapper
              routeName='Legacy Workspace Selection Redirect'
              fallbackRoute='/user-onboarding'
            >
              <ProtectedRoute>
                <UserOnboardingWithProvider />
              </ProtectedRoute>
            </RouteWrapper>
          }
        />

        <Route
          path='/checkout'
          element={
            <RouteWrapper routeName='Checkout' fallbackRoute='/profile/pricing'>
              <ProtectedRoute>
                <StripeFeatureErrorBoundary featureName='Checkout'>
                  <Checkout />
                </StripeFeatureErrorBoundary>
              </ProtectedRoute>
            </RouteWrapper>
          }
        />
        <Route
          path='/return'
          element={
            <RouteWrapper routeName='Payment Return' fallbackRoute='/profile/pricing'>
              <ProtectedRoute>
                <Return />
              </ProtectedRoute>
            </RouteWrapper>
          }
        />

        {/* Catch-all route for 404 errors */}
        <Route
          path='*'
          element={
            <RouteWrapper routeName='404 Not Found' fallbackRoute='/chat'>
              <div className='flex flex-col items-center justify-center h-screen'>
                <h1 className='text-2xl font-bold mb-4'>Page Not Found</h1>
                <p>The page you are looking for does not exist.</p>
                <Link to='/' className='mt-4 text-blue-500 hover:underline'>
                  Return to Home
                </Link>
              </div>
            </RouteWrapper>
          }
        />
      </Routes>
    </Suspense>
  );
};
